/**
 * TemplateManager — save and load reusable configuration templates.
 *
 * Renders as a compact dropdown-style UI with:
 *   - "Save as Template" button + name input
 *   - List of available templates to load or delete
 *
 * Props:
 *   templateType — "normMap", "buyerRatings", "ratingScale", or "calcParams"
 *   getCurrentData — function that returns the current config data to save
 *   label — display label (e.g., "Document Type Mappings")
 */
import { useState, useEffect } from "react";
import { useDispatch } from "react-redux";
import {
  listTemplates,
  saveTemplate,
  loadTemplate,
  deleteTemplate,
} from "Redux/project/actions";

const TemplateManager = ({ templateType, getCurrentData, label }) => {
  const dispatch = useDispatch();
  const [isOpen, setIsOpen] = useState(false);
  const [templates, setTemplates] = useState([]);
  const [saveName, setSaveName] = useState("");
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(null);

  const refreshList = () => {
    dispatch(
      listTemplates(templateType, (list) => {
        setTemplates(list);
      })
    );
  };

  useEffect(() => {
    if (isOpen) refreshList();
  }, [isOpen]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleSave = () => {
    if (!saveName.trim()) return;
    setSaving(true);
    const data = getCurrentData();
    dispatch(
      saveTemplate(saveName.trim(), templateType, data, () => {
        setSaving(false);
        setSaveName("");
        refreshList();
      })
    );
  };

  const handleLoad = (templateid) => {
    setLoading(templateid);
    dispatch(
      loadTemplate(templateid, templateType, () => {
        setLoading(null);
      })
    );
  };

  const handleDelete = (templateid) => {
    dispatch(
      deleteTemplate(templateid, () => {
        refreshList();
      })
    );
  };

  return (
    <div style={{ position: "relative", display: "inline-block" }}>
      <button
        className="button is-small"
        onClick={() => setIsOpen(!isOpen)}
        style={{ borderRadius: "6px" }}
      >
        <span className="icon" style={{ fontSize: "0.8rem" }}>
          <i className="fa-duotone fa-bookmark" />
        </span>
        <span>Templates</span>
        <span className="icon" style={{ fontSize: "0.6rem", marginLeft: "0.2rem" }}>
          <i className={isOpen ? "fa-solid fa-chevron-up" : "fa-solid fa-chevron-down"} />
        </span>
      </button>

      {isOpen && (
        <div
          style={{
            position: "absolute",
            top: "100%",
            right: 0,
            marginTop: "0.3rem",
            width: "300px",
            backgroundColor: "var(--ev-white)",
            border: "1px solid var(--ev-border)",
            borderRadius: "var(--ev-radius)",
            boxShadow: "var(--ev-shadow-lg)",
            zIndex: 50,
            padding: "0.75rem",
          }}
        >
          {/* Header */}
          <div style={{
            fontSize: "0.75rem", fontWeight: 700, color: "var(--ev-text)",
            marginBottom: "0.5rem", display: "flex", justifyContent: "space-between", alignItems: "center",
          }}>
            <span>{label} Templates</span>
            <button
              className="delete is-small"
              onClick={() => setIsOpen(false)}
            />
          </div>

          {/* Save section */}
          <div style={{ display: "flex", gap: "0.3rem", marginBottom: "0.6rem" }}>
            <input
              className="input is-small"
              type="text"
              placeholder="Template name..."
              value={saveName}
              onChange={(e) => setSaveName(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && handleSave()}
              style={{ flex: 1, borderRadius: "5px" }}
            />
            <button
              className={`button is-small is-success ${saving ? "is-loading" : ""}`}
              onClick={handleSave}
              disabled={!saveName.trim() || saving}
              style={{ borderRadius: "5px" }}
            >
              Save
            </button>
          </div>

          {/* Template list */}
          {templates.length === 0 ? (
            <div style={{
              fontSize: "0.72rem", color: "var(--ev-text-muted)",
              fontStyle: "italic", padding: "0.3rem 0",
            }}>
              No saved templates
            </div>
          ) : (
            <div style={{ maxHeight: "200px", overflowY: "auto" }}>
              {templates.map((t) => (
                <div
                  key={t._id}
                  style={{
                    display: "flex", alignItems: "center", gap: "0.3rem",
                    padding: "0.35rem 0.4rem", borderRadius: "5px",
                    marginBottom: "0.2rem",
                    backgroundColor: "var(--ev-surface)",
                  }}
                >
                  <span style={{ flex: 1, fontSize: "0.75rem", fontWeight: 600 }}>
                    {t.name}
                  </span>
                  <button
                    className={`button is-small ${loading === t._id ? "is-loading" : ""}`}
                    onClick={() => handleLoad(t._id)}
                    style={{ borderRadius: "4px", padding: "0 0.5em", height: "22px", fontSize: "0.65rem" }}
                  >
                    Load
                  </button>
                  <button
                    className="button is-small is-danger is-light"
                    onClick={() => handleDelete(t._id)}
                    style={{ borderRadius: "4px", padding: "0 0.4em", height: "22px", fontSize: "0.6rem" }}
                  >
                    <i className="fa-solid fa-xmark" />
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default TemplateManager;
